import { useEffect, useRef } from "react";
import { Animated, Easing, StyleSheet, View } from "react-native";
import Svg, {
  Circle as SvgCircle,
  Defs,
  RadialGradient,
  Stop,
} from "react-native-svg";

import { MicrophoneIcon } from "../icons";
import { useColors } from "../theme";

interface Props {
  listening: boolean;
  /** Orb diameter — 120 mobile, 144 web/wide (design_handoff_2 §7). */
  size?: number;
}

// Voice orb. Radial gradient (lighter peach top → coral bottom) with a
// centered microphone glyph. An expanding ring breathes outward around
// the orb — the orb itself stays at rest; only the ring carries the
// listening pulse. Pauses cleanly when not listening.
export function VoiceOrb({ listening, size = 120 }: Props): JSX.Element {
  const colors = useColors();
  const pulse = useRef(new Animated.Value(0)).current;
  const r = size / 2;
  const glyphSize = Math.round(size / 3);

  useEffect(() => {
    if (!listening) {
      pulse.stopAnimation();
      pulse.setValue(0);
      return;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 1100,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: 1100,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [listening, pulse]);

  // Ring grows from the orb's outer edge to +26px outward and fades.
  // Matches the prototype's `box-shadow: 0 0 0 26px transparent` keyframe.
  const ringScale = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1 + 26 / r],
  });
  const ringOpacity = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: [0.45, 0],
  });

  return (
    <View
      style={[styles.wrap, { width: size + 52, height: size + 52 }]}
    >
      <Animated.View
        style={[
          { position: "absolute", width: size, height: size, borderRadius: r },
          { backgroundColor: colors.action },
          { transform: [{ scale: ringScale }], opacity: ringOpacity },
        ]}
      />
      <View
        style={[styles.orb, { width: size, height: size, borderRadius: r }]}
      >
        <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <Defs>
            <RadialGradient
              id="voiceOrbGradient"
              cx="50%"
              cy="40%"
              r="60%"
              fx="50%"
              fy="40%"
            >
              <Stop offset="0%" stopColor="#FF8C6F" />
              <Stop offset="100%" stopColor={colors.action} />
            </RadialGradient>
          </Defs>
          <SvgCircle cx={r} cy={r} r={r} fill="url(#voiceOrbGradient)" />
        </Svg>
        <View style={styles.glyph}>
          <MicrophoneIcon size={glyphSize} color="#FFFFFF" strokeWidth={1.8} />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 26,
  },
  orb: {
    alignItems: "center",
    justifyContent: "center",
  },
  glyph: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
  },
});
